import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { AnimatePresence, motion } from 'motion/react';
import { trackEvent } from '@shared/analytics';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import type { UnsubscribeAction } from '../api/emailApi';
import { useEmailLinkQuery, useEmailSubscriptionMutation } from '../model/queries';

/** Запись истории страницы: токен из письма, убранный из адреса, и последнее выполненное действие. */
interface EmailLinkState {
  emailToken?: string;
  emailAction?: UnsubscribeAction;
}

function linkState(state: unknown): EmailLinkState {
  const raw = (state ?? {}) as EmailLinkState;
  return {
    emailToken: typeof raw.emailToken === 'string' && raw.emailToken ? raw.emailToken : undefined,
    emailAction: raw.emailAction === 'resubscribe' || raw.emailAction === 'unsubscribe' ? raw.emailAction : undefined,
  };
}

type View = 'pending' | 'off' | 'on' | 'invalid' | 'error';

const PRIMARY =
  'inline-flex w-full items-center justify-center gap-2 rounded-pill bg-accent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg-inv transition-colors hover:bg-accent-hi sm:w-auto';
const SECONDARY =
  'relative inline-flex w-full items-center justify-center gap-2 rounded-pill border border-solid border-line bg-transparent px-7 py-4 text-[15px] font-medium tracking-[0.02em] text-fg transition-colors hover:bg-elev disabled:cursor-default sm:w-auto';

export function UnsubscribeContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  // Токен из письма — секрет: читаем один раз и сразу убираем из адреса, как сброс пароля. Он
  // остаётся в history.state этой записи: перезагрузка повторяет последнее действие, «Назад» не
  // возвращает токен в адрес, а значит и в аналитику (AnalyticsTracker молчит, пока он в адресе).
  const [token] = useState(() => searchParams.get('token') || linkState(location.state).emailToken || null);
  const [initialAction] = useState<UnsubscribeAction>(() => linkState(location.state).emailAction ?? 'unsubscribe');
  const [subscribed, setSubscribed] = useState(initialAction === 'resubscribe');
  const tokenInUrl = searchParams.has('token');

  const link = useEmailLinkQuery(token, initialAction);
  const toggle = useEmailSubscriptionMutation(token);

  useEffect(() => {
    if (!token || !tokenInUrl) return;
    const state: EmailLinkState = { emailToken: token, emailAction: initialAction };
    setSearchParams({}, { replace: true, state });
  }, [token, tokenInUrl, initialAction, setSearchParams]);

  const errorCode = link.error?.code;
  const view: View = !token
    ? 'invalid'
    : link.isPending || (link.isError && link.isFetching) // первый запрос или «Попробовать ещё раз»
      ? 'pending'
      : link.isError
        ? errorCode === 'INVALID_EMAIL_TOKEN'
          ? 'invalid'
          : 'error'
        : subscribed
          ? 'on'
          : 'off';

  // Один просмотр на заход — когда исход известен и токена в адресе уже нет.
  const reported = useRef(false);
  useEffect(() => {
    if (reported.current || tokenInUrl || view === 'pending') return;
    reported.current = true;
    const result = view === 'off' ? 'unsubscribed' : view === 'on' ? 'resubscribed' : view;
    trackEvent('unsubscribe_page_viewed', { result });
  }, [view, tokenInUrl]);

  const switchTo = (action: UnsubscribeAction) => {
    if (!token || toggle.isPending) return;
    toggle.mutate(action, {
      onSuccess: () => {
        setSubscribed(action === 'resubscribe');
        // Перезагрузка страницы повторит это действие, а не исходную отписку.
        const state: EmailLinkState = { emailToken: token, emailAction: action };
        navigate({ pathname: location.pathname, search: '' }, { replace: true, state });
        if (action === 'resubscribe') trackEvent('resubscribed');
        else trackEvent('reminder_toggle_changed', { enabled: false, place: 'unsubscribe_page' });
      },
    });
  };

  const toggleError = toggle.isError
    ? t(toggle.error?.code === 'RATE_LIMIT' ? 'unsubscribe.error.rateLimit' : 'unsubscribe.toggleError')
    : null;

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={view}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.28 }}
        className="flex w-full max-w-[420px] flex-col gap-[18px]"
      >
        {view === 'pending' && (
          <div role="status" className="flex flex-col gap-4">
            <span
              aria-hidden
              className="block h-10 w-10 animate-spin rounded-full border-2 border-line border-t-accent"
            />
            <div className="text-[15px] text-muted">
              {t(initialAction === 'resubscribe' ? 'unsubscribe.saving' : 'unsubscribe.pending')}
            </div>
          </div>
        )}

        {(view === 'off' || view === 'on') && (
          <>
            <Badge tone="success">✓</Badge>
            <Title>{t(view === 'off' ? 'unsubscribe.off.title' : 'unsubscribe.on.title')}</Title>
            <Body>{t(view === 'off' ? 'unsubscribe.off.body' : 'unsubscribe.on.body')}</Body>
            <Actions>
              <Link to={paths.dashboard} className={PRIMARY}>
                {t('unsubscribe.open')}
              </Link>
              <button
                type="button"
                className={SECONDARY}
                disabled={toggle.isPending}
                aria-busy={toggle.isPending}
                onClick={() => switchTo(view === 'off' ? 'resubscribe' : 'unsubscribe')}
              >
                {/* Подпись остаётся невидимой и держит ширину кнопки, пока идёт запрос. */}
                <span className={cn(toggle.isPending && 'invisible')}>
                  {t(view === 'off' ? 'unsubscribe.resubscribe' : 'unsubscribe.unsubscribeAgain')}
                </span>
                {toggle.isPending && (
                  <span className="absolute inset-0 flex items-center justify-center gap-2">
                    <span
                      aria-hidden
                      className="block h-4 w-4 animate-spin rounded-full border-2 border-line border-t-accent"
                    />
                    {t('unsubscribe.saving')}
                  </span>
                )}
              </button>
            </Actions>
            {toggleError && (
              <div role="alert" className="text-[14px] text-danger">
                {toggleError}
              </div>
            )}
          </>
        )}

        {view === 'invalid' && (
          <>
            <Badge tone="danger">!</Badge>
            <Title>{t('unsubscribe.invalid.title')}</Title>
            <Body>{t('unsubscribe.invalid.body')}</Body>
            <Actions>
              <Link to={paths.profile} className={PRIMARY}>
                {t('unsubscribe.invalid.settings')}
              </Link>
              <Link to={paths.dashboard} className={SECONDARY}>
                {t('unsubscribe.open')}
              </Link>
            </Actions>
          </>
        )}

        {view === 'error' && (
          <>
            <Badge tone="danger">!</Badge>
            <Title>{t('unsubscribe.error.title')}</Title>
            <Body>{t(errorCode === 'RATE_LIMIT' ? 'unsubscribe.error.rateLimit' : 'unsubscribe.error.body')}</Body>
            <Actions>
              <button type="button" className={PRIMARY} onClick={() => void link.refetch()}>
                {t('unsubscribe.error.retry')}
              </button>
              <Link to={paths.dashboard} className={SECONDARY}>
                {t('unsubscribe.open')}
              </Link>
            </Actions>
          </>
        )}
      </motion.div>
    </AnimatePresence>
  );
}

function Badge({ tone, children }: { tone: 'success' | 'danger'; children: ReactNode }) {
  return (
    <div
      aria-hidden
      className={cn(
        'inline-flex h-16 w-16 items-center justify-center rounded-[20px] text-3xl',
        tone === 'success' ? 'bg-success/15 text-success' : 'bg-danger/15 text-danger',
      )}
    >
      {children}
    </div>
  );
}

function Title({ children }: { children: ReactNode }) {
  return (
    <h1 className="font-serif text-[28px] font-medium leading-[1.1] tracking-[-0.02em] sm:text-[38px]">{children}</h1>
  );
}

function Body({ children }: { children: ReactNode }) {
  return <p className="text-[15px] leading-[1.6] text-muted">{children}</p>;
}

function Actions({ children }: { children: ReactNode }) {
  return <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:flex-wrap">{children}</div>;
}
